/**
 * Text — eine gespeicherte Wahrheit.
 *
 * In cubic projects lag eine Beschreibung doppelt vor: als ProseMirror-Struktur
 * und als abgeleiteter Klartext. Metarr erzeugte Klartext, den der Server
 * anschließend umwandelte. Jede dieser Übersetzungen ist eine Stelle, an der
 * Inhalt still verloren gehen kann.
 *
 * Hier ist **Markdown die gespeicherte Fassung**. Das Modell schreibt sie
 * direkt, die Schnittstelle liest und schreibt sie unverändert, die Suche
 * greift darauf zu, und der Verlauf zeigt lesbare Unterschiede.
 *
 * Zwei Dinge kann reines Markdown nicht, und beide sind bei uns fachlich
 * nötig. Dafür gibt es genau zwei Direktiven — mehr nicht, und ausdrücklich
 * kein offenes MDX: beliebige Komponenten im Inhalt wären bei strikter CSP
 * und einem schreibenden Assistenten eine Auswertungsfläche.
 *
 *   :marke[zitierter Text]{#<uuid>}
 *       Eine Textstelle. Randkommentare und Klärungen hängen daran. Die Marke
 *       steht **im Inhalt**, damit sie mit dem Text wandert, statt an einer
 *       Zeichenposition zu kleben, die der nächste Absatz verschiebt.
 *
 *   :verweis{art=aufgabe kennung=CUPR-84}
 *       Ein Referenz-Chip. Gespeichert wird die Kennung, **nicht** die
 *       Beschriftung — aufgelöst wird beim Anzeigen, mit Sichtbarkeitsprüfung.
 *       Im Bestand standen an dreizehn Stellen tote Kennungen, weil das Label
 *       beim Speichern eingefroren wurde.
 */
import { z } from "zod";
import { OBJEKTARTEN } from "./kanten.js";
/* -------------------------------------------------------------------------
 * Schemata
 * ---------------------------------------------------------------------- */
/** Ein Beschreibungstext. Obergrenze großzügig, aber nicht unbegrenzt. */
export const markdownSchema = z.string().max(200_000);
/** Ein kurzer Text — Kommentar, Kriterium, Begründung. */
export const markdownKurzSchema = z.string().trim().min(1).max(20_000);
/* -------------------------------------------------------------------------
 * Marken (Textstellen)
 * ---------------------------------------------------------------------- */
const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
/**
 * `:marke[Zitat]{#uuid}` — die eckige Klammer darf mit `\]` maskiert werden,
 * damit ein Zitat selbst Klammern enthalten kann.
 */
const MARKE = new RegExp(String.raw `:marke\[((?:\\.|[^\\\]])*)\]\{#(${UUID})\}`, "gi");
/** Alle Marken eines Textes, in Reihenfolge ihres Auftretens. */
export function marken(markdown) {
    const gefunden = [];
    for (const treffer of markdown.matchAll(MARKE)) {
        const zitat = treffer[1] ?? "";
        const id = treffer[2];
        if (id)
            gefunden.push({ id: id.toLowerCase(), zitat: entmaskiere(zitat) });
    }
    return gefunden;
}
/** Nur die Kennungen — für den Vorher-Nachher-Vergleich. */
export function markenIds(markdown) {
    return new Set(marken(markdown).map((m) => m.id));
}
/**
 * **Die Invariante.**
 *
 * Markdown kennt keine Inline-Spanne mit Kennung — eine Marke ist hier Text,
 * und Text kann ein Modell beim Umformulieren verlieren. Deshalb prüft der
 * Server jeden Schreibvorgang: Verschwindet eine Marke, wird der Schreibvorgang
 * **abgelehnt**, nicht stillschweigend übernommen.
 *
 * Ohne diese Prüfung verlöre ein Randkommentar seine Stelle, ohne dass es
 * jemand merkt — derselbe Fehler, den der Bestand als „Verweise ohne Halt"
 * kennt. Eine Marke bewusst zu entfernen bleibt möglich: dann wird zuerst der
 * Faden aufgelöst und danach der Text geschrieben.
 */
export function pruefeMarken(vorher, nachher) {
    const alt = markenIds(vorher);
    const neu = markenIds(nachher);
    const verschwunden = [...alt].filter((id) => !neu.has(id));
    const hinzugekommen = [...neu].filter((id) => !alt.has(id));
    return { ok: verschwunden.length === 0, verschwunden, hinzugekommen };
}
/** Eine Marke um eine Textstelle legen. */
export function setzeMarke(zitat, id) {
    return `:marke[${maskiere(zitat)}]{#${id.toLowerCase()}}`;
}
/**
 * Eine Marke entfernen und ihr Zitat als reinen Text stehen lassen — der
 * bewusste Weg, wenn ein Faden aufgelöst wurde.
 */
export function loeseMarke(markdown, id) {
    const gezielt = new RegExp(String.raw `:marke\[((?:\\.|[^\\\]])*)\]\{#${id.toLowerCase()}\}`, "gi");
    return markdown.replace(gezielt, (_, zitat) => entmaskiere(zitat));
}
/* -------------------------------------------------------------------------
 * Verweise (Referenz-Chips)
 * ---------------------------------------------------------------------- */
const VERWEIS = /:verweis\{([^}]*)\}/g;
export const verweisSchema = z.object({
    art: z.enum(OBJEKTARTEN),
    /** Menschenlesbare Kennung wie „CUPR-84" — bevorzugt, weil prüfbar. */
    kennung: z.string().trim().min(1).max(64).nullish(),
    /** Alternativ die Kennung des Objekts, wenn es keine sprechende gibt. */
    id: z.uuid().nullish(),
});
/**
 * Alle Verweise eines Textes — das, was beim Anzeigen aufgelöst werden muss.
 *
 * Aufgelöst wird **mit Sichtbarkeitsprüfung des lesenden Benutzers**. Ein
 * Verweis auf ein Objekt, das jemand nicht sehen darf, wird zur neutralen
 * Kennung ohne Titel — nicht zu einem Chip, der den Titel verrät.
 */
export function verweise(markdown) {
    const gefunden = [];
    for (const treffer of markdown.matchAll(VERWEIS)) {
        const attribute = leseAttribute(treffer[1] ?? "");
        const art = attribute["art"];
        if (!art || !OBJEKTARTEN.includes(art))
            continue;
        const kennung = attribute["kennung"];
        const id = attribute["id"];
        if (!kennung && !id)
            continue;
        gefunden.push({
            art: art,
            kennung: kennung ?? null,
            id: id ?? null,
        });
    }
    return gefunden;
}
/** Einen Verweis schreiben. Bevorzugt die sprechende Kennung. */
export function setzeVerweis(verweis) {
    const teile = [`art=${verweis.art}`];
    if (verweis.kennung)
        teile.push(`kennung=${verweis.kennung}`);
    else if (verweis.id)
        teile.push(`id=${verweis.id}`);
    return `:verweis{${teile.join(" ")}}`;
}
function leseAttribute(roh) {
    const attribute = {};
    for (const paar of roh.trim().split(/\s+/)) {
        const trenner = paar.indexOf("=");
        if (trenner <= 0)
            continue;
        const schluessel = paar.slice(0, trenner);
        const wert = paar.slice(trenner + 1);
        if (schluessel && wert)
            attribute[schluessel] = wert;
    }
    return attribute;
}
/* -------------------------------------------------------------------------
 * Klartext
 * ---------------------------------------------------------------------- */
/**
 * Der Text ohne Direktiven — für Suche, Vorschau und Benachrichtigungen.
 *
 * Ausdrücklich **keine zweite gespeicherte Fassung**: Das hier wird bei Bedarf
 * berechnet, nicht in einer Spalte gehalten. Genau diese Spalte war im Bestand
 * die zweite Wahrheit, die auseinanderlaufen konnte.
 */
export function klartext(markdown) {
    return markdown
        .replace(MARKE, (_, zitat) => entmaskiere(zitat))
        .replace(VERWEIS, (_, roh) => {
        const attribute = leseAttribute(roh);
        return attribute["kennung"] ?? "";
    })
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/`([^`]*)`/g, "$1")
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/^#{1,6}\s+/gm, "")
        .replace(/^\s{0,3}>\s?/gm, "")
        .replace(/(\*\*|__|\*|_|~~)/g, "")
        .replace(/\s+/g, " ")
        .trim();
}
/* -------------------------------------------------------------------------
 * Maskierung
 * ---------------------------------------------------------------------- */
/** Eckige Klammern und Rückstriche in einem Zitat unschädlich machen. */
export function maskiere(text) {
    return text.replace(/([\\\]])/g, "\\$1");
}
function entmaskiere(text) {
    return text.replace(/\\([\\\]])/g, "$1");
}
//# sourceMappingURL=text.js.map